import { createSlice } from '@reduxjs/toolkit'
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction'
import store from 'src/store'
import {
  builderSagaCommunications,
  clearAllStateCommunications,
} from 'src/helpers/redux'
import { actions as moduleActions } from './actions'
import * as moduleState from './state'
import moduleSagas from './sagas'
import * as moduleSelectors from './selectors'
import { nanoid } from 'nanoid'
import { IResponse } from 'src/services/accounts/http'

export const slice = createSlice({
  name: moduleState.PREFIX,
  initialState: moduleState.initialState,
  reducers: {
    setSchemas(
      state: moduleState.IState,
      action: PayloadAction<{
        data: IResponse<moduleState.IStateData['schemas']['items']>
        showInMenu?: boolean
      }>
    ) {
      const payload = {
        items: action.payload.data.data,
        cursor: action.payload.data.meta.nextCursor,
      }
      if (action.payload.showInMenu) {
        state.data.schemas = {
          items: [...state.data.schemas.items, ...payload.items],
          cursor: payload.cursor,
        }
      } else {
        state.data.schemasAdmin = {
          items: [...state.data.schemasAdmin.items, ...payload.items],
          cursor: payload.cursor,
        }
      }
    },
    mergeSchemas(
      state: moduleState.IState,
      action: PayloadAction<{
        data: IResponse<moduleState.IStateData['schemas']['items']>
        showInMenu?: boolean
      }>
    ) {
      const newSchemas: moduleState.IStateData['schemas']['items'] = []
      const uniqIds = new Set(
        (action.payload.showInMenu
          ? state.data.schemas.items
          : state.data.schemasAdmin.items
        ).map((i) => i.identifier)
      )

      action.payload.data.data.forEach((item) => {
        if (!uniqIds.has(item.identifier)) {
          newSchemas.push(item)
          uniqIds.add(item.identifier)
        }
      })

      if (action.payload.showInMenu) {
        state.data.schemas = {
          items: [...state.data.schemas.items, ...newSchemas],
          cursor: action.payload.data.meta.nextCursor,
        }
      } else {
        state.data.schemasAdmin = {
          items: [...state.data.schemasAdmin.items, ...newSchemas],
          cursor: action.payload.data.meta.nextCursor,
        }
      }
    },

    setSchemesByType(
      state: moduleState.IState,
      action: PayloadAction<{
        data: IResponse<moduleState.IStateData['schemas']['items']>
      }>
    ) {
      const newSchemas: moduleState.IStateData['schemas']['items'] = []
      const uniqIds = new Set(state.data.schemas.items.map((i) => i.identifier))

      action.payload.data.data.forEach((item) => {
        if (!uniqIds.has(item.identifier)) {
          newSchemas.push(item)
          uniqIds.add(item.identifier)
        }
      })

      state.data.schemas.items = [...state.data.schemas.items, ...newSchemas]
    },

    setSingleSchema(
      state: moduleState.IState,
      action: PayloadAction<{
        data: moduleState.IStateData['schemas']['items'][number]
        isAdmin?: boolean
      }>
    ) {
      if (action.payload.isAdmin) {
        state.data.schemasAdmin = {
          ...state.data.schemasAdmin,
          items: [...state.data.schemasAdmin.items, action.payload.data],
        }
      } else {
        state.data.schemas = {
          ...state.data.schemas,
          items: [...state.data.schemas.items, action.payload.data],
        }
      }
    },

    setSchemasAppend(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['schemas']['items'][number]>
    ) {
      if (action.payload.parentIdentifier) {
        state.data.schemas.items = state.data.schemas.items.map((item) => {
          if (item.identifier === action.payload.parentIdentifier) {
            return {
              ...item,
              children: [...item.children, action.payload],
            }
          }

          return item
        })
      }
      if (action.payload.showInMenu) {
        state.data.schemas.items.push(action.payload)
      }
      state.data.schemasAdmin.items.push(action.payload)
    },
    setSchemasUpdate(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['schemas']['items'][number]>
    ) {
      state.data.schemas.items = state.data.schemas.items.map((item) => {
        if (item.identifier === action.payload.identifier) {
          return {
            ...item,
            ...action.payload,
          }
        }

        return item
      })
      state.data.schemasAdmin.items = state.data.schemasAdmin.items.map(
        (item) => {
          if (item.identifier === action.payload.identifier) {
            return {
              ...item,
              ...action.payload,
            }
          }

          return item
        }
      )
    },
    setSchemasDelete(
      state: moduleState.IState,
      action: PayloadAction<{ identifier: string }>
    ) {
      state.data.schemas.items = state.data.schemas.items.filter(
        (item) => item.identifier !== action.payload.identifier
      )

      state.data.schemasAdmin.items = state.data.schemasAdmin.items.filter(
        (item) => item.identifier !== action.payload.identifier
      )
    },
    setToastId(
      state: moduleState.IState,
      action: PayloadAction<{ key: string; id?: string | number }>
    ) {
      const { key, id } = action.payload

      if (id) {
        state.data.toasts[key] = id
      } else {
        delete state.data.toasts[key]
      }
    },
    setFieldsGroups(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['fieldsGroups'][string]>
    ) {
      state.data.fieldsGroups[action.payload.schemaName] = action.payload
    },
    setClear(state: moduleState.IState) {
      state.data = moduleState.getInitialData()
      clearAllStateCommunications(state.communication)
    },
    setLocation(
      state: moduleState.IState,
      action: PayloadAction<{
        path?: string
        search?: Record<string, any>
        key?: string
        replaced?: boolean
      }>
    ) {
      // if (action.payload.key){
      state.data.location = { ...action.payload, key: nanoid() }
      // } else {
      //   state.data.location = {
      //     ...state.data.location,
      //     ...action.payload,
      //     search: {
      //       ...decodeSearch(),
      //       ...(action.payload.search || {})
      //     },
      //     replaced: action.payload.replaced || false,
      //     key: nanoid()
      //   }
      // }
    },
    setIconSearch(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['icons']['search']>
    ) {
      state.data.icons.search = action.payload
    },
    setIconSearchMore(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['icons']['search']>
    ) {
      state.data.icons.search = {
        params: action.payload.params,
        result: {
          icons: [
            ...state.data.icons.search.result.icons,
            ...action.payload.result.icons,
          ],
        },
      }
    },
    setIconCollection(
      state: moduleState.IState,
      action: PayloadAction<moduleState.IStateData['icons']['collection']>
    ) {
      state.data.icons.collection = action.payload
    },

    setSmartListsCount(
      state,
      action: PayloadAction<{ schemeName: string; number: number }>
    ) {
      state.data.schemas.items = state.data.schemas.items.map((i) =>
        i.identifier === action.payload.schemeName
          ? { ...i, smartListsCount: i.smartListsCount + action.payload.number }
          : i
      )
    },

    setStageFieldCount(
      state,
      action: PayloadAction<{ schemeName: string; number: number }>
    ) {
      state.data.schemas.items = state.data.schemas.items.map((i) =>
        i.identifier === action.payload.schemeName
          ? {
              ...i,
              stageFieldsCount: i.smartListsCount + action.payload.number,
            }
          : i
      )
    },
  },
  extraReducers: (builder) => {
    builderSagaCommunications<moduleState.IState>(builder, moduleActions)
  },
})

export const setters = slice.actions
export const actions = moduleActions
export const reducers = { [moduleState.PREFIX]: slice.reducer }
export const sagas = moduleSagas
export const state = moduleState
export const selectors = moduleSelectors

export const injectStore = () => {
  store.injectReducer?.(moduleState.PREFIX, slice.reducer)
  store.injectSaga?.(moduleState.PREFIX, sagas)
}
